import React, {
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Form,
  InputGroup,
  ListGroup,
  ListGroupItem,
  Popover,
  PopoverBody,
} from 'reactstrap';
import { useFeatures, setAccessToken } from '@cordata/buffet';
import { capitalize } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import kebabcaseKeys from 'kebabcase-keys';

import {
  authorizedInstance as rb,
  authorizedPyInstance as py,
  useDebounce,
} from '~/utils';

import AdvancedSearchModal from '~/components/Patient/AdvancedSearchModal';
import PatientsBannerModal from '~/components/Patient/Identity/Banner/Modal';

import PatientQuickSearchResult from './QuickSearchResult';

const PatientQuickSearch = ({ theme }) => {
  const { hasAppIdentity } = useFeatures();
  const history = useHistory();
  const [t] = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [bannerModalPatient, setBannerModalPatient] = useState();
  const [isSearching, setIsSearching] = useState(false);

  const togglePopover = () => {
    // don't close popover when closing banner modal
    if (!isBannerModalOpen) {
      setIsPopoverOpen(!isPopoverOpen);
    }
  };

  const toggleAdvancedSearchModal = () => setIsAdvancedSearchOpen(!isAdvancedSearchOpen);

  const toggleBannerModal = () => {
    setBannerModalPatient(undefined);
    setIsBannerModalOpen(!isBannerModalOpen);
  };

  const switchDivisionAndNavigate = (targetDivisionUid, patientId) => {
    // Copy Navbar implementation
    const c = JSON.parse(localStorage.getItem('current')).data;

    const accessibleDivisions =
      c.relationships?.user?.relationships?.accessibleDivisions?.data || [];

    const targetDivision = accessibleDivisions.find(d => d.id === targetDivisionUid);

    if (!targetDivision) {
      console.error('Target division not accessible:', targetDivisionUid);
      return;
    }

    c.relationships.currentDivision = { data: { type: 'divisions', id: targetDivisionUid } };

    rb
      .put('/session/current', kebabcaseKeys({ data: c }, { deep: true }))
      .then(r => r.data.data.attributes)
      .then((r) => {
        localStorage.clear();
        setAccessToken(r.accessToken);
        window.location = `/patients/${patientId}`;
      })
      .catch(console.error);
  };

  useEffect(() => {
    setIsBannerModalOpen(!!bannerModalPatient);
  }, [bannerModalPatient]);

  useEffect(() => {
    if (searchQuery.length > 1) {
      if (hasAppIdentity) {
        py
          .get(`api/data/public/identity-quick-search?q=${searchQuery}`)
          .then(r => r.data.data)
          .then((r) => {
            setPatients(r);
            setIsSearching(false);
            setIsPopoverOpen(true);
          });
      } else {
        rb
          .get(`summary-patients?sort=last-name,first-name&q=${searchQuery}`)
          .then(r => r.data.data)
          .then((r) => {
            setPatients(r);
            setIsSearching(false);
            setIsPopoverOpen(true);
          });
      }
    } else if (searchQuery.length === 1) {
      setPatients();
      setIsSearching(false);
      setIsPopoverOpen(true);
    } else {
      setIsSearching(false);
      setIsPopoverOpen(false);
    }
  }, [searchQuery]);

  const debouncedSearch = useDebounce((value) => {
    setIsSearching(true);
    setPatients([]);
    setSearchQuery(value);
  }, 900);

  return (
    <React.Fragment>
      <Form inline={theme.inline} id="quick-search-form" onSubmit={f => f.preventDefault()}>
        <InputGroup size={theme.size} className="w-100">
          <Input placeholder={`Search ${capitalize(t('patients'))}`} onChange={evt => debouncedSearch(evt.target.value)} />
        </InputGroup>

        <Popover
          target="quick-search-form"
          placement="bottom"
          container="inline" // necessary for useFeatures to work in QuickSearchResult
          isOpen={isPopoverOpen}
          toggle={togglePopover}
          hideArrow
          className="my-0 rounded-0 w-100 mh-50v oy-auto"
        >
          <PopoverBody className="p-0">
            <ListGroup tag="div" flush>
              {(patients && patients.length) ? patients.map((patient, index) => {
                  const patientKey = patient.id || patient.result?.attributes?.patientUid;

                  return (
                    <PatientQuickSearchResult
                      key={patientKey || `patient-${index}`}
                      data={patient}
                      onClick={() => {
                        try {
                          const patientId = patient.id || patient.result.attributes.patientUid;
                          const patientDivisionUid = hasAppIdentity
                            ? patient.result?.attributes?.divisionUid
                            : patient.attributes?.divisionUid;

                          // Get current division
                          const currentUser = JSON.parse(localStorage.getItem('current')).data;
                          const { currentDivisionUid } = currentUser.attributes;

                          setIsPopoverOpen(false);

                          // Identity users: keep existing behavior (CIT-focused fix)
                          if (hasAppIdentity) {
                            setBannerModalPatient(patientId);
                            return;
                          }

                          // CIT users: check division before direct navigation
                          if (patientDivisionUid && patientDivisionUid !== currentDivisionUid) {
                            // Switch division then navigate
                            switchDivisionAndNavigate(patientDivisionUid, patientId);
                          } else {
                            // Same division: direct navigation
                            history.push(`/patients/${patientId}`);
                          }
                        } catch (error) {
                          console.error('Patient click error:', error);
                        }
                      }}
                    />
                  );
                }) : (
                  <ListGroupItem
                    className="d-flex justify-content-between align-items-center"
                  >
                    {searchQuery.length > 1 && !isSearching ? 'No results found.' : 'Please enter at least 2 characters.'}
                  </ListGroupItem>
              )}
            </ListGroup>
          </PopoverBody>
        </Popover>
      </Form>

      <AdvancedSearchModal
        isOpen={isAdvancedSearchOpen}
        toggleIsOpen={toggleAdvancedSearchModal}
      />

      {bannerModalPatient && (
        <PatientsBannerModal
          isOpen={isBannerModalOpen}
          toggleIsOpen={toggleBannerModal}
          patient={bannerModalPatient}

          onCancel={() => {
            setIsBannerModalOpen(false);
          }}
          onContinue={() => {
            setIsBannerModalOpen(false);
            setIsPopoverOpen(false);
          }}
        />
      )}
    </React.Fragment>
  );
};

PatientQuickSearch.propTypes = {
  theme: PropTypes.shape({
    color: PropTypes.string,
    size: PropTypes.string,
    inline: PropTypes.bool,
  }),
};

PatientQuickSearch.defaultProps = {
  theme: {
    color: 'secondary',
    size: undefined,
  },
};

export default PatientQuickSearch;
